import base64 from 'hi-base64';
import * as R from 'ramda';
import {BehaviorSubject, throwError, timer, from} from 'rxjs';
import {catchError, flatMap, publishReplay, refCount, startWith, take, tap} from 'rxjs/operators';

import {makeCache} from 'iss/utils/makeCache';
import rxFetch from 'iss/utils/rxFetch';
import * as aad from 'iss/utils/azureAd';

export const baseURL = "/api/v1";
const httpAuthHeaders = {
};
const jsonHeaders = {
  'Accept': 'application/json',
  'Content-Type': 'application/json'
};

let fetchImplementation = rxFetch;

/** Only used in unit tests */
export function useMockFetchImplementation(mock) {
  fetchImplementation = mock;
}
/** Only used in unit tests */
export function restoreFetchImplementation() {
  fetchImplementation = rxFetch;
}

export function setAuthenticationDetails(username, password) {
  httpAuthHeaders["Authorization"] = "Basic "+base64.encode(username+":"+password);
}

export function clearAuthenticationDetails() {
  delete httpAuthHeaders["Authorization"];
  clearAllCaches();
}

export function hasAuthenticationDetails() {
  return !!httpAuthHeaders["Authorization"];
}

function patchMethod(object, method, patcher) {
  const originalMethod = object[method].bind(object);
  object[method] = patcher(originalMethod);

  return object;
}

function addSideEffect(sideEffect) {
  return func => (...args) => {
    sideEffect();
    return func(...args);
  };
}

let currentRequestCount = 0;
let currentRequestNumber = 0;
const requests = {};
const requestCountSubject = new BehaviorSubject(0);

function countCalls(observable, method, url) {
  const requestNumber = ++currentRequestNumber;
  observable = patchMethod(observable, "subscribe", addSideEffect(requestStarted));
  observable = patchMethod(observable, "subscribe", subscribe => {
    return (...args) => {
      const subscription = subscribe(...args);
      return patchMethod(subscription, "unsubscribe", addSideEffect(requestFinished));
    };
  });

  function requestStarted() {
    requestCountSubject.next(++currentRequestCount);
    requests[requestNumber] = method+" "+url;
  }

  const requestFinished = R.once(() => {
    requestCountSubject.next(--currentRequestCount);
    delete requests[requestNumber];
  });

  return observable.pipe(tap(requestFinished, requestFinished));
}

const fetchJson = R.curry(function fetchJson (method, url, body = undefined, {resultType = "json", bodyType, ...options} = {})
{
  // Initially this was implemented so that first an async getAuthHeaders() function (which depending on
  // aad.isSignedIn() asynchronously acquired the token or immediately returned httpAuthHeaders) was
  // first in the observable chain and that was then mapped to the actual request.
  // However, that broke the unit tests: it seems that the tests assume that requests are immediate
  // (with the mock fetch implementation) and they do toHaveBeenCalled() calls before the request was
  // even created (with the correct headers).
  // There probably are ways of getting the tests to work with non-immediate fetches, but that probably
  // would require changing all the tests (?). So instead if not using AAD, then this function behaves
  // as it did before => tests work.
  function createRequest(authHeaders) {
    const headers = R.mergeAll([jsonHeaders, authHeaders, options.headers]);

    // If sending form data, let FormData handle headers automatically
    if (body instanceof FormData) {
      delete headers['Content-Type'];
    }
    let mergedOptions = R.mergeRight(options, {
      method,
      body: R.unless(b => b === "" || bodyType === "plain", JSON.stringify, body),
      headers
    });

    return fetchImplementation(baseURL+url, mergedOptions)[resultType]();
  }

  let req;

  if (aad.isSignedIn()) {
    req = from(aad.acquireToken())
      .pipe(flatMap(token => createRequest({'Authorization': `Bearer ${token}`})))
      .pipe(catchError(err => {

        if (err.response && err.response.status == 401) {
          return from(aad.acquireToken(null, true))
            .pipe(flatMap(token => createRequest({'Authorization': `Bearer ${token}`})));

        } else {
          throw err;
        }

      }));
  } else {
    req = createRequest(httpAuthHeaders);
  }

  req = req.pipe(catchError(processError));

  if (options.showLoadingScreen === false) {
    return req;
  } else {
    return countCalls(req, method, url);
  }
});

function processError(err) {
  if (!err.response) {
    return throwError({
      isError: true,
      status: "network-problem",
      message: "Network failed"
    });
  }

  const requestData = {
    isError: true,
    statusCode: err.response.status
  };

  return err.response.json()
            .pipe(flatMap(json => throwError(R.mergeRight(requestData, json))));
}

export const getJson = (url, options = {}) => fetchJson('GET', url, undefined, options);

// Response is an object with fields 'blob', 'type' and 'filename'.
// The latter 2 fields are defined only the response headers defines the Content-Disposition header.
export const getBlob = (url, options = {}) => fetchJson('GET', url, undefined, {...options, resultType: "blob"});

export const postMultipart = (url, body) => fetchJson('POST', url, body, {bodyType: "plain"});
export const postJson = fetchJson('POST');
export const putJson = fetchJson('PUT');
export const deleteJson = fetchJson('DELETE');
export const requestCount = requestCountSubject.asObservable();

/* Exports currently running requests as strings of form "METHOD /url/to/api" for debugging */
export function getOnGoingRequests() {
  return R.values(requests);
}

const caches = [];

export function clearAllCaches() {
  caches.map(cache => cache.clearCache());
}

export function monitorUrl(url, cacheTime, config = {}) {
  const observable = makeCache(() => {
    return timer(cacheTime, cacheTime).pipe(
      startWith(0),
      flatMap(() => getJson(url, config)),
      publishReplay(1,cacheTime),
      refCount()
    );
  });
  caches.push(observable);
  return observable;
}

export function cache(url, cacheTime, config = {}) {
  const monitor = monitorUrl(url, cacheTime, config);
  const cache = monitor.pipe(take(1));
  cache.clearCache = monitor.clearCache;
  return cache;
}

export const MINUTES = 60*1000;
export const HOURS = 60*MINUTES;

export default {
  setAuthenticationDetails,
  clearAuthenticationDetails,
  hasAuthenticationDetails,
  requestCount,
  getJson,
  getBlob,
  postMultipart,
  postJson,
  putJson,
  deleteJson,
  cache,
  monitorUrl,
  MINUTES,
  HOURS
};



// WEBPACK FOOTER //
// ./modules/issFrontend/utils/ajax.js